import * as React from 'react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';
import { LanguageTab, LanguageTabs } from './DocsLanguageTabsWidget';

enum DiffView {
  COMBINED = 'Combined',
  BEFORE = 'Before',
  AFTER = 'After'
}

type CodeBlockProps = React.ComponentProps<'pre'> & { 'data-diff-code'?: string };

/**
 * Keep the highlighted tokens while filtering diff lines and rebuilding their separators.
 */
const selectVersion = (children: React.ReactNode, view: DiffView): React.ReactNode => {
  if (view === DiffView.COMBINED) return children;
  return React.Children.map(children, (child) => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return child;
    const lines = React.Children.toArray(child.props.children).filter((line) => {
      if (!React.isValidElement<{ className?: string }>(line)) return false;
      const classes = line.props.className?.split(' ') ?? [];
      return (
        classes.includes('diff-line') && !classes.includes(view === DiffView.BEFORE ? 'diff-added' : 'diff-removed')
      );
    });
    return React.cloneElement(
      child,
      {},
      lines.flatMap((line, index) => (index === 0 ? [line] : ['\n', line]))
    );
  });
};

export const DocsCodeBlockWidget: React.FC<CodeBlockProps> = (props) => {
  if (props['data-diff-code'] === undefined) return <CodeBlock {...props} />;

  const lines = React.Children.toArray(props.children).flatMap((child) => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return [];
    return React.Children.toArray(child.props.children).filter(
      (line): line is React.ReactElement<{ className?: string }> =>
        React.isValidElement<{ className?: string }>(line) &&
        (line.props.className?.split(' ').includes('diff-line') ?? false)
    );
  });
  const onlyAdditions =
    lines.length > 0 && lines.every((line) => line.props.className?.split(' ').includes('diff-added'));

  if (onlyAdditions) return <CodeBlock {...props} view={DiffView.COMBINED} />;

  return (
    <LanguageTabs defaultValue={DiffView.COMBINED} label="Choose code version">
      {Object.values(DiffView).map((view) => (
        <LanguageTab key={view} label={view}>
          <CodeBlock {...props} view={view}>
            {selectVersion(props.children, view)}
          </CodeBlock>
        </LanguageTab>
      ))}
    </LanguageTabs>
  );
};

const CodeBlock: React.FC<CodeBlockProps & { view?: DiffView }> = ({ view, ...props }) => {
  const preRef = React.useRef<HTMLPreElement>(null);
  const [status, setStatus] = React.useState('');
  const diffCode = props['data-diff-code'];
  const label =
    diffCode === undefined ? 'Copy code' : view === DiffView.BEFORE ? 'Copy before code' : 'Copy after code';

  React.useEffect(() => setStatus(''), [view]);

  React.useEffect(() => {
    if (!status) return;
    const timeout = window.setTimeout(() => setStatus(''), 2000);
    return () => window.clearTimeout(timeout);
  }, [status]);

  return (
    <S.Block data-code-block data-diff-view={view}>
      <pre {...props} ref={preRef} />
      <S.CopyButton
        data-code-copy
        type="button"
        aria-label={label}
        title={status || label}
        onClick={async () => {
          const code = view === DiffView.COMBINED ? (diffCode ?? '') : (preRef.current?.textContent ?? '');
          try {
            await navigator.clipboard.writeText(code);
            setStatus('Copied');
          } catch {
            setStatus('Copy unavailable. Select the code manually.');
          }
        }}
      >
        <FontAwesomeIcon icon={status === 'Copied' ? faCheck : faCopy} />
      </S.CopyButton>
      <S.Status role="status">{status}</S.Status>
    </S.Block>
  );
};

namespace S {
  export const CopyButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    padding: 0;
    cursor: pointer;
    border: 1px solid ${(p) => p.theme.text.codeInlineBorder};
    border-radius: 6px;
    background: ${(p) => p.theme.text.codeBlockBackground};
    color: ${(p) => p.theme.text.codeColor};
    opacity: 0;
    transition: opacity 150ms ease;

    @media (hover: none) {
      opacity: 1;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `;

  export const Block = styled.div`
    position: relative;
    display: flow-root;
    margin: 1em 0;

    pre {
      margin: 0;
    }
    &:hover button,
    &:focus-within button {
      opacity: 1;
    }
    &[data-diff-view='Before'],
    &[data-diff-view='After'] {
      .diff-line {
        background: transparent;
      }
      .diff-line::before {
        display: none;
      }
    }
    .diff-line {
      box-sizing: border-box;
      display: inline-block;
      min-width: 100%;
      padding-right: 12px;
    }
    .diff-line::before {
      content: attr(data-marker);
      display: inline-block;
      width: 1.5em;
      user-select: none;
      -webkit-user-select: none;
    }
    .diff-added {
      background: rgba(46, 160, 67, 0.19);
    }
    .diff-added::before {
      color: ${(p) => (p.theme.light ? '#1a7f37' : '#7ee787')};
    }
    .diff-removed {
      background: rgba(248, 81, 73, 0.19);
    }
    .diff-removed::before {
      color: ${(p) => (p.theme.light ? '#cf222e' : '#ff7b72')};
    }
  `;

  export const Status = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  `;
}
